import { useEffect, useRef, useState } from "react";
import { Button, PlayButton, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { formatTime } from "../../utils/strings";
import { getOrganizationFile, isOrganizationFileUrl } from "../../utils/organizationFiles";

type VoicePreviewActionsProps = {
  showDownload?: boolean;
  onDownload?: () => void;
  onRemove?: () => void;
};

const VoicePreviewActions = ({ showDownload = true, onDownload, onRemove }: VoicePreviewActionsProps) => {
  if (!showDownload && !onRemove) {
    return null;
  }

  return (
    <View className="flex items-center gap-2 self-end sm:self-center shrink-0">
      {showDownload && onDownload && (
        <Button
          onClick={onDownload}
          variant="ghost"
          className="!p-2 !rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Download audio"
        >
          <Icon
            name="download"
            size={18}
            className="text-gray-500 dark:text-gray-400"
          />
        </Button>
      )}
      {onRemove && (
        <Button
          onClick={onRemove}
          variant="ghost"
          className="!p-1.5 !rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Remove selected audio"
        >
          <Icon
            name="close"
            size={16}
            className="text-gray-500 dark:text-gray-400"
          />
        </Button>
      )}
    </View>
  );
};

type VoicePreviewProps = {
  audioUrl?: string | null;
  audioKey: string;
  onPlaySample: (key: string, audio: HTMLAudioElement) => void;
  isActive: boolean;
  filename?: string;
  onClose?: () => void;
  showDownload?: boolean;
  variant?: "bar" | "button";
};

export const VoicePreview = ({ audioUrl, audioKey, onPlaySample, isActive, filename, onClose, showDownload = true, variant = "bar" }: VoicePreviewProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);
  const objectUrlRef = useRef<{ url: string; revoke: () => void } | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState<string | null>(() => (audioUrl && audioUrl.startsWith("blob:") ? audioUrl : null));

  const releaseObjectUrl = () => {
    if (objectUrlRef.current) {
      objectUrlRef.current.revoke();
      objectUrlRef.current = null;
    }
  };

  const detachAudioListeners = () => {
    if (cleanupListenersRef.current) {
      cleanupListenersRef.current();
      cleanupListenersRef.current = null;
    }
  };

  const teardownAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audioRef.current = null;
    }
    detachAudioListeners();
  };

  const resetPlaybackState = () => {
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
  };

  useEffect(() => {
    return () => {
      teardownAudio();
      releaseObjectUrl();
    };
  }, [audioKey]);

  useEffect(() => {
    resetPlaybackState();
    setHasError(false);
    setIsLoading(false);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    teardownAudio();

    if (!audioUrl) {
      setResolvedAudioUrl(null);
      releaseObjectUrl();
      return;
    }

    if (audioUrl.startsWith("blob:")) {
      setResolvedAudioUrl(audioUrl);
    } else {
      setResolvedAudioUrl(null);
      releaseObjectUrl();
    }
  }, [audioUrl]);

  const bindAudioEvents = (audio: HTMLAudioElement) => {
    const handleLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handlePlay = () => {
      setIsPlaying(true);
    };
    const handlePause = () => {
      if (audioRef.current !== audio) {
        return;
      }
      setIsPlaying(false);
    };
    const handleEnded = () => {
      if (audioRef.current !== audio) {
        return;
      }
      setIsPlaying(false);
      setCurrentTime(audio.duration || 0);
    };
    const handleError = () => {
      setHasError(true);
      setIsLoading(false);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    cleanupListenersRef.current = () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };

    if (audio.readyState >= 1) {
      handleLoadedMetadata();
    }
  };

  const ensureResolvedAudioUrl = async (): Promise<string | null> => {
    if (!audioUrl) {
      return null;
    }

    if (resolvedAudioUrl) {
      return resolvedAudioUrl;
    }

    if (audioUrl.startsWith("blob:")) {
      setResolvedAudioUrl(audioUrl);
      return audioUrl;
    }

    if (isOrganizationFileUrl(audioUrl)) {
      try {
        const { objectUrl, revokeObjectUrl } = await getOrganizationFile(audioUrl);
        releaseObjectUrl();
        objectUrlRef.current = { url: objectUrl, revoke: revokeObjectUrl };
        setResolvedAudioUrl(objectUrl);
        return objectUrl;
      } catch (error) {
        throw error;
      }
    }

    try {
      const directResponse = await fetch(audioUrl);
      if (!directResponse.ok) {
        throw new Error(`Failed to fetch audio from provided URL: ${directResponse.status}`);
      }

      const blob = await directResponse.blob();
      const objectUrl = URL.createObjectURL(blob);
      releaseObjectUrl();
      objectUrlRef.current = { url: objectUrl, revoke: () => URL.revokeObjectURL(objectUrl) };
      setResolvedAudioUrl(objectUrl);
      return objectUrl;
    } catch (error) {
      throw error;
    }
  };

  const loadAudioIfNeeded = async (): Promise<HTMLAudioElement | null> => {
    if (!audioUrl) {
      return null;
    }

    if (audioRef.current) {
      return audioRef.current;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const source = await ensureResolvedAudioUrl();
      if (!source) {
        throw new Error("Audio source unavailable");
      }

      const audio = new Audio(source);
      audio.preload = "auto";
      audioRef.current = audio;
      bindAudioEvents(audio);
      return audio;
    } catch (error) {
      console.error("Error loading audio sample:", error);
      teardownAudio();
      setHasError(true);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive && isPlaying && audioRef.current) {
      audioRef.current.pause();
    }
  }, [isActive, isPlaying]);

  const onTogglePlayback = async () => {
    if (!audioUrl || isLoading) {
      return;
    }

    if (isPlaying) {
      audioRef.current?.pause();
      return;
    }

    try {
      const audio = await loadAudioIfNeeded();
      if (!audio) {
        return;
      }

      onPlaySample(audioKey, audio);
      await audio.play();
    } catch (error) {
      console.error("Error playing audio sample:", error);
      setHasError(true);
    }
  };

  const onProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || duration === 0 || hasError) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const clickPosition = event.clientX - rect.left;
    const percentage = Math.min(Math.max(clickPosition / rect.width, 0), 1);
    const newTime = percentage * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const onDownload = async () => {
    if (!audioUrl) {
      return;
    }

    try {
      let downloadUrl = resolvedAudioUrl;

      if (!downloadUrl) {
        downloadUrl = await ensureResolvedAudioUrl();
      }

      if (!downloadUrl) {
        throw new Error("Unable to resolve audio for download");
      }

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || `${audioKey}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading audio sample:", error);
    }
  };

  if (!audioUrl) {
    return null;
  }

  const previewActions = (showDownload || onClose) && (
    <VoicePreviewActions
      showDownload={showDownload}
      onDownload={onDownload}
      onRemove={onClose}
    />
  );

  const barVariantContent = (
    <View className="w-full flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <View className="flex w-full items-center gap-3 sm:flex-1">
        <Button
          onClick={onTogglePlayback}
          variant="ghost"
          className="!p-2 !w-9 !h-9 !rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          isDisabled={isLoading}
        >
          <Icon
            name={isPlaying ? "pause" : "play"}
            size={18}
            className="text-brand dark:text-brand-light"
          />
        </Button>
        <View className="flex-1 space-y-2">
          {filename && <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{filename}</Text>}
          <View
            className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden cursor-pointer"
            onClick={onProgressClick}
          >
            <View
              className="h-full bg-brand dark:bg-brand-light transition-all"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </View>
          <View className="flex justify-between">
            <Text className="text-xs text-gray-500 dark:text-gray-400">{formatTime(currentTime)}</Text>
            <Text className="text-xs text-gray-500 dark:text-gray-400">{duration > 0 ? formatTime(duration) : isLoading ? "Loading..." : "--:--"}</Text>
          </View>
        </View>
      </View>
      {previewActions}
    </View>
  );

  const buttonVariantContent = (
    <PlayButton
      isPlaying={isPlaying}
      onPlayPause={onTogglePlayback}
      audioElement={audioRef.current}
      audioUrl={resolvedAudioUrl ?? undefined}
      className="!rounded-full"
      isLoading={isLoading}
    />
  );

  return hasError ? (
    <Text className="text-sm text-red-500 dark:text-red-400">Unable to load audio preview.</Text>
  ) : variant === "button" ? (
    buttonVariantContent
  ) : (
    barVariantContent
  );
};
